import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  listModels,
  getModelPreferences,
  putModelPreferences,
} from '../api/adaFetch';
import { useAuth } from '../contexts/Auth';
import {
  AUTO_MODEL_SENTINEL,
  COMPOSER_VARIANTS,
  DEFAULT_COMPOSER_ID,
  resolveComposerId,
} from './composers';
import { applyModelPrefs, emptyModelPrefs } from './modelPrefs';
import {
  clampThinkingId,
  filterCatalogModels,
  thinkingLevelsForModel,
  thinkingDefaultForModel,
} from './providerCapabilities';
import { DEFAULT_THINKING, THINKING_LEVELS } from './thinking';

import { normalizeMediaSettings } from './mediaOptions';

const ChatPrefsContext = createContext(null);
const COMPOSER_KEY = 'ada-composer-variant';
const MODEL_KEY = 'ada-chat-model';
const THINKING_KEY = 'ada-thinking-level';

/** Preferred default when the catalog offers it (Ada product). */
const PREFERRED_DEFAULT_MODEL = 'ada-x-fast';

function readComposerId() {
  if (typeof window === 'undefined') return DEFAULT_COMPOSER_ID;
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('composer');
  if (fromQuery) return resolveComposerId(fromQuery);
  const saved = localStorage.getItem(COMPOSER_KEY);
  if (saved) return resolveComposerId(saved);
  return DEFAULT_COMPOSER_ID;
}

function readModelId() {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem(MODEL_KEY);
  if (!stored || stored === AUTO_MODEL_SENTINEL) return '';
  return stored;
}

export function ChatPrefsProvider({ children }) {
  const { token, user } = useAuth();
  const currentUser = useRef(user?.id);
  currentUser.current = user?.id;
  const [composerId, setComposerIdState] = useState(readComposerId);
  const [modelId, setModelIdState] = useState(readModelId);
  const [thinkingPreference, setThinkingState] = useState(
    () => localStorage.getItem(THINKING_KEY) || DEFAULT_THINKING
  );
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState(emptyModelPrefs);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(null);

  const setComposerId = useCallback((id) => {
    const resolved = resolveComposerId(id);
    if (!COMPOSER_VARIANTS.some((v) => v.id === resolved)) return;
    setComposerIdState(resolved);
    localStorage.setItem(COMPOSER_KEY, resolved);
    const url = new URL(window.location.href);
    url.searchParams.set('composer', resolved);
    window.history.replaceState({}, '', url);
  }, []);

  /** Empty / falsy id = Auto (omit model on prompt). */
  const setModelId = useCallback((id) => {
    if (!id) {
      setModelIdState('');
      localStorage.setItem(MODEL_KEY, AUTO_MODEL_SENTINEL);
      return;
    }
    setModelIdState(id);
    localStorage.setItem(MODEL_KEY, id);
  }, []);

  const setThinking = useCallback((level) => {
    if (!THINKING_LEVELS.some((t) => t.id === level)) return;
    setThinkingState(level);
    localStorage.setItem(THINKING_KEY, level);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setCatalog([]);
      return undefined;
    }
    setModelsLoading(true);
    listModels(token)
      .then((data) => {
        if (cancelled) return;
        const raw = Array.isArray(data?.models)
          ? data.models
          : Array.isArray(data)
            ? data
            : [];
        // Hide xAI redirect-only slugs (grok-3*, grok-4 → grok-4.3) from lasagna.
        const list = filterCatalogModels(raw);
        setCatalog(list);
        setModelsError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load models', err);
        setModelsError(err.message || 'Failed to load models');
        setCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const [preferencesReady, setPreferencesReady] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesError, setPreferencesError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setPrefs(emptyModelPrefs());
    setPreferencesReady(false);
    setPreferencesSaving(false);
    setPreferencesLoading(false);
    setPreferencesError(null);
    if (!token) return undefined;
    setPreferencesLoading(true);
    getModelPreferences(token)
      .then((data) => {
        if (!cancelled) {
          setPrefs(data);
          setPreferencesReady(true);
        }
      })
      .catch((err) => {
        if (!cancelled) setPreferencesError(err.message);
      })
      .finally(() => {
        if (!cancelled) setPreferencesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, user?.id]);
  const saveModelPreferences = useCallback(
    async (next) => {
      const owner = user?.id;
      setPreferencesSaving(true);
      try {
        const saved = await putModelPreferences(next, token);
        if (currentUser.current === owner) {
          setPrefs(saved);
          setPreferencesError(null);
        }
      } catch (err) {
        if (currentUser.current === owner) setPreferencesError(err.message);
        throw err;
      } finally {
        if (currentUser.current === owner) setPreferencesSaving(false);
      }
    },
    [token, user?.id]
  );

  const models = useMemo(
    () => applyModelPrefs(catalog, prefs),
    [catalog, prefs]
  );

  useEffect(() => {
    if (!catalog.length) return;
    setModelIdState((current) => {
      const stored = localStorage.getItem(MODEL_KEY);
      if (stored === AUTO_MODEL_SENTINEL) return '';
      if (stored && models.some((model) => model.id === stored)) return stored;
      if (current && models.some((m) => m.id === current)) return current;
      const def =
        models.find((m) => m.id === PREFERRED_DEFAULT_MODEL) ||
        models.find((m) => m.default) ||
        models[0];
      if (def?.id) {
        localStorage.setItem(MODEL_KEY, def.id);
        return def.id;
      }
      return '';
    });
  }, [models, catalog.length]);

  const selectedModel = useMemo(
    () => models.find((m) => m.id === modelId) || null,
    [models, modelId]
  );
  const [mediaPreferences, setMediaPreferences] = useState({});
  const mediaSettings = useMemo(
    () =>
      normalizeMediaSettings(selectedModel?.media, mediaPreferences[modelId]),
    [selectedModel, mediaPreferences, modelId]
  );
  const setMediaSettings = useCallback(
    (next) => setMediaPreferences((prev) => ({ ...prev, [modelId]: next })),
    [modelId]
  );
  const isAutoModel = !modelId;

  const thinkingLevels = useMemo(
    () =>
      isAutoModel || selectedModel?.media
        ? []
        : thinkingLevelsForModel(selectedModel),
    [isAutoModel, selectedModel]
  );
  const defaultThinking = thinkingDefaultForModel(selectedModel);
  // Clamp during render so a model switch cannot send the old model's level.
  const thinking = clampThinkingId(
    thinkingPreference,
    thinkingLevels,
    defaultThinking
  );

  useEffect(() => {
    if (!thinkingLevels.length) return;
    if (thinking !== thinkingPreference) setThinking(thinking);
  }, [thinkingLevels, thinking, thinkingPreference, setThinking]);

  const value = useMemo(
    () => ({
      mediaSettings,
      setMediaSettings,
      composerId,
      composerVariants: COMPOSER_VARIANTS,
      setComposerId,
      modelId,
      setModelId,
      isAutoModel,
      selectedModel,
      thinking,
      setThinking,
      /** Levels valid for the current model; empty when Auto. */
      thinkingLevels,
      defaultThinking,
      /** Full catalog (off/low/standard/high) for reference. */
      allThinkingLevels: THINKING_LEVELS,
      models,
      catalog,
      modelsLoading,
      modelsError,
      modelPreferences: prefs,
      preferencesReady,
      preferencesLoading,
      preferencesSaving,
      preferencesError,
      saveModelPreferences,
    }),
    [
      mediaSettings,
      setMediaSettings,
      composerId,
      setComposerId,
      modelId,
      setModelId,
      isAutoModel,
      selectedModel,
      thinking,
      setThinking,
      thinkingLevels,
      defaultThinking,
      models,
      catalog,
      modelsLoading,
      modelsError,
      prefs,
      preferencesReady,
      preferencesLoading,
      preferencesSaving,
      preferencesError,
      saveModelPreferences,
    ]
  );

  return (
    <ChatPrefsContext.Provider value={value}>
      {children}
    </ChatPrefsContext.Provider>
  );
}

export function useChatPrefs() {
  const ctx = useContext(ChatPrefsContext);
  if (!ctx)
    throw new Error('useChatPrefs must be used within ChatPrefsProvider');
  return ctx;
}
